import { bookAwaitingPurchase } from '@/lib/orders/book-status-guard';
import { getImageUrl } from '@/utils/secure-image-proxy';
import type { MangaPageScript } from '@/types/manga';
import { comicShape } from '@/types/manga';
import { READER_BAND_ORDER, type ReaderBand } from '@/utils/manga/comic-story-profile';

export type ComicReaderState = 'writing' | 'failed' | 'awaiting-purchase' | 'drawing' | 'retrying' | 'ready';

/** A paid comic in PROCESSING_ERROR is re-polled this often (the stuck-paid sweep may re-queue it). */
export const RETRYING_POLL_MS = 60_000;
export const COMIC_RETRYING_MESSAGE =
  "We hit a snag drawing your comic — we're retrying automatically. You'll get an email when it's ready.";
/** An unpaid preview that failed: no refund promise, no "we've been notified". */
export const COMIC_PREVIEW_FAILED_MESSAGE =
  "We couldn't finish this comic preview. Please try again with a different photo or story idea.";

export const FAILED_AFTER_MS = 45 * 60 * 1000;

interface ReaderPage {
  pageNumber?: number;
  page_number?: number;
  imageUrl?: string | null;
  image_url?: string | null;
}

/**
 * Where a comic is in its life, from the book as GET /api/books/[bookId] returns it.
 * PROCESSING_ERROR on a purchased comic is 'retrying' however old (the worker alerted the admin and the
 * stuck-paid-books sweep may re-queue it). `purchased` is the server's view-access (paid or comp order), never
 * metadata. Otherwise PROCESSING_ERROR counts as drawing while fresh and as failed once it has sat untouched
 * for FAILED_AFTER_MS.
 */
export function comicReaderState(
  book: { status?: string | null; purchased?: boolean | null; pages?: ReaderPage[]; updated_at?: string | null; updatedAt?: string | null } | null,
  now: number = Date.now()
): ComicReaderState {
  if (!book || book.status === 'DRAFT') return 'writing';
  if (book.status === 'FAILED') return 'failed';
  if (book.status === 'PROCESSING_ERROR') {
    if (book.purchased === true) return 'retrying';
    // Retries are exhausted once the worker marks it failed; a stale error is terminal, a fresh one is retried.
    const at = Date.parse(String(book.updatedAt ?? book.updated_at ?? ''));
    if (Number.isFinite(at) && now - at > FAILED_AFTER_MS) return 'failed';
  }
  if (bookAwaitingPurchase(book.status)) return 'awaiting-purchase';
  const interior = (book.pages ?? []).filter((pg) => (pg.pageNumber ?? pg.page_number ?? 0) > 0);
  const allDrawn = interior.length > 0 && interior.every((pg) => !!(pg.imageUrl || pg.image_url));
  return allDrawn ? 'ready' : 'drawing';
}

export type ComicResumeTarget = 'preview' | 'reader' | 'failed';

/**
 * Where /create-comic goes when it re-polls a stored preview book: keep waiting for the preview while it is
 * written or ready to buy, the reader once it has been bought (drawing, ready, or retrying), failed for a
 * failed preview.
 */
export function comicResumeTarget(status: string | null | undefined): ComicResumeTarget {
  if (status === 'FAILED') return 'failed';
  if (!status || bookAwaitingPurchase(status)) return 'preview';
  return 'reader';
}

export type PollFailureAction = 'continue' | 'stop-invalid' | 'stop-lost';

/** What the preview poll does after a failed GET /api/books/[id]. `consecutive` counts this failure. */
export function pollFailureAction(status: number | 'network', consecutive: number): PollFailureAction {
  if (status === 403 || status === 404) return 'stop-invalid';
  return consecutive >= 3 ? 'stop-lost' : 'continue';
}

export const POLL_INVALID_MESSAGE = 'This comic link is no longer valid in this browser. Please start again.';
export const POLL_LOST_MESSAGE = 'We lost touch with your comic. Please try again.';

/** User-facing message for a failed POST /api/books/create-manga-preview, and whether going back can help. */
export function previewErrorMessage(status: number, body: any): { message: string; goBack: boolean } {
  if (status === 429) {
    return { message: "You've made a few comics in a short time. Please wait a little while and try again.", goBack: false };
  }
  if (status === 413) {
    return { message: 'That photo is too big. Please go back and choose a smaller one.', goBack: true };
  }
  if (status === 400) {
    const text = typeof body?.error === 'string' && body.error.trim() ? body.error.trim() : '';
    return { message: text || 'Please check your photo and details.', goBack: true };
  }
  if (status === 404) return { message: "Comics aren't available right now.", goBack: false };
  if (status >= 500) return { message: 'Our comic studio is busy. Please try again in a minute.', goBack: false };
  return { message: 'Something went wrong while starting your comic. Please try again.', goBack: false };
}

/** The honest promise under a comic preview: only the cover, title and logline exist before the order. */
export function comicPreviewPromise(pageCount: number): string {
  return `Your full ${pageCount}-page story is written and drawn after you order.`;
}

/**
 * What the preview shows, from the book as GET /api/books/[id] returns it. The page count comes from the book
 * (a pitch has no pages), never from script.pages.length. `legacyPages` is non-empty only for a flag-off book.
 */
export function comicPreviewFromBook(
  book: any,
  fallback: { title: string; pageCount: number }
): { title: string; coverImageUrl: string | null; logline: string; pageCount: number; legacyPages: MangaPageScript[] } {
  const manga = book?.metadata?.manga;
  const script = manga?.script;
  const cover = (book?.pages ?? []).find((pg: any) => (pg.pageNumber ?? pg.page_number) === 0);
  const coverUrl = cover?.imageUrl ?? cover?.image_url;
  return {
    title: script?.title || book?.title || fallback.title,
    coverImageUrl: coverUrl ? getImageUrl(coverUrl) : null,
    logline: script?.logline || '',
    pageCount: book?.page_count ?? book?.pageCount ?? manga?.settings?.pageCount ?? fallback.pageCount,
    legacyPages: Array.isArray(script?.pages) ? script.pages : [],
  };
}

/**
 * The reader band to offer a "change" link for: only a storyboard V2 comic in PREVIEW_READY whose story pages have no
 * images. The stamp and band come from the polled book's metadata; null hides the link.
 */
export function comicChangeableBand(book: any): ReaderBand | null {
  if (!book || book.status !== 'PREVIEW_READY') return null;
  const settings = book.metadata?.manga?.settings ?? book.metadata?.manga_settings;
  if (settings?.storyboard !== 'v2') return null;
  const band = settings.readerBand;
  if (!READER_BAND_ORDER.includes(band)) return null;
  const drawn = (book.pages ?? []).some(
    (pg: ReaderPage) => (pg.pageNumber ?? pg.page_number ?? 0) > 0 && !!(pg.imageUrl || pg.image_url)
  );
  return drawn ? null : band;
}

/** Preview poll interval: fast previews are ready in about a minute, so poll every 2 s. */
export function comicPreviewPollMs(fast: boolean): number {
  return fast ? 2000 : 4000;
}

/** The reader's drawing banner text. With no interior rows yet the page script is still being written. */
export function comicDrawingLabel(drawn: number, interiorRows: number, pageCount: number): string {
  return interiorRows === 0
    ? `Writing your story, then drawing all ${pageCount} pages`
    : `${drawn} of ${interiorRows} pages drawn`;
}

/** The print-ready notice in the reader: square comics print 8x8, tall comics A5. The page count decides. */
export function comicPrintReadyNotice(pageCount: number | null | undefined): string {
  const format = comicShape({ pageCount: pageCount ?? undefined }) === 'square' ? '8×8 (200 × 200 mm)' : 'A5';
  return `The print-ready ${format} PDF of this comic is ready for a print shop.`;
}

/** The paid digital PDF download (same route the picture-book readers use; authorizes via owner/cookie/?bt=). */
export function comicPdfDownloadHref(bookId: string, accessToken?: string | null): string {
  return `/api/books/pdf-download?bookId=${encodeURIComponent(bookId)}${accessToken ? `&bt=${encodeURIComponent(accessToken)}` : ''}`;
}

/** Offer the PDF only for a finished comic whose API response carried a signed pdfUrl (null for shared/unpaid views). */
export function showComicPdfDownload(state: ComicReaderState, pdfUrl: string | null | undefined): boolean {
  return state === 'ready' && !!pdfUrl;
}

export const BAND_RETRY_MS = 10_000;
export const BAND_RETRY_WINDOW_MS = 5 * 60 * 1000;

/** After a failed band change: retry only a "busy" 409 (the story is being written), for up to 5 minutes. */
export function bandChangeRetry(status: number, body: any, elapsedMs: number): 'retry' | 'stop' {
  return status === 409 && body?.error === 'busy' && elapsedMs < BAND_RETRY_WINDOW_MS ? 'retry' : 'stop';
}

export const BAND_LIMIT_MESSAGE = "You've changed the age a few times; please start a new comic to change it again.";

/** The message shown after a band change that did not go through. */
export function bandChangeErrorMessage(body: any): string {
  if (body?.error === 'busy') return 'The story is still being written. Please try again in a minute.';
  if (body?.error === 'limit') return BAND_LIMIT_MESSAGE;
  return typeof body?.error === 'string' && body.error ? body.error : 'We could not change that. Please try again.';
}

/** The most cover redraws a comic gets (the server's COMIC_MAX_COVER_REDRAWS, services/comic-cover-redraw.ts). */
export const COMIC_MAX_COVER_REDRAWS = 3;

/**
 * Cover redraws a comic preview still has (3 minus metadata.manga.coverRedraws, never below 0), or null when the cover
 * cannot be redrawn at all: not PREVIEW_READY, or a story page is already drawn. Null hides the button.
 */
export function comicCoverRedrawsLeft(book: any): number | null {
  if (!book || book.status !== 'PREVIEW_READY') return null;
  const drawn = (book.pages ?? []).some(
    (pg: ReaderPage) => (pg.pageNumber ?? pg.page_number ?? 0) > 0 && !!(pg.imageUrl || pg.image_url)
  );
  if (drawn) return null;
  return Math.max(0, COMIC_MAX_COVER_REDRAWS - (Number(book.metadata?.manga?.coverRedraws) || 0));
}

/** The note under "Redraw the cover". */
export function coverRedrawNote(left: number): string {
  if (left <= 0) return "You've used all 3 cover redraws for this comic.";
  return `Something look wrong, like a missing arm or a floating hand? Get a new cover with the same story. ${left} redraw${left === 1 ? '' : 's'} left.`;
}

/** Shown before a redraw goes ahead: why the button exists, and that the old cover is gone for good. */
export const COVER_REDRAW_GONE_WARNING = "The cover you see now will be replaced and can't be brought back, even if you like it more than the new one.";

export function coverRedrawConfirmText(left: number): string {
  return `Redraw is for covers that came out wrong, like a missing arm, an extra hand or a face that doesn't look right. ${COVER_REDRAW_GONE_WARNING} You have ${left} redraw${left === 1 ? '' : 's'} left.`;
}

/** The server's COMIC_REDRAW_STALE_MS: a pending redraw older than this died (preview lock TTL 900 s + 120 s). */
export const COMIC_REDRAW_STALE_MS = (900 + 120) * 1000;

/** A DRAFT comic whose cover redraw died: the poll asks the redraw route once to put the preview back. */
export function comicRedrawStuck(book: any, now: number = Date.now()): boolean {
  const pending = book?.metadata?.manga?.coverRedrawPending;
  if (!book || book.status !== 'DRAFT' || !pending) return false;
  const at = Date.parse(String(pending.at ?? ''));
  return !Number.isFinite(at) || now - at > COMIC_REDRAW_STALE_MS;
}

export const COVER_REDRAW_RECOVERED_MESSAGE = 'Your last redraw did not finish, so we kept your cover. You can try again.';

export const COVER_REDRAW_KEPT_MESSAGE = "We couldn't draw a new cover this time, so we kept this one. Please try again.";

/** The message shown after a redraw request that did not go through. */
export function coverRedrawErrorMessage(body: any): string {
  if (body?.error === 'busy') return 'Your story is still being written. Please try again in a minute.';
  if (body?.error === 'limit') return coverRedrawNote(0);
  if (body?.error === 'not-changeable') return 'This cover can no longer be changed.';
  return 'We could not redraw the cover. Please try again.';
}
